export type PortalDocumentMode = 'view' | 'accept' | 'comment';
export type PortalDocumentTarget = 'proposal' | 'change_order';

// Looks up a document's own URL, for openers that bypass the portal's document page.
export type PortalDocumentUrlResolver = (
	quoteId: number | string,
	mode: PortalDocumentMode,
	target: PortalDocumentTarget,
	done: (url: string | null) => void
) => void;

// The portal's own page for reading one of a quote's documents. Opened in a new tab,
// so the customer keeps their place in the portal — and the address bar stays on a
// portal URL, because that page frames the document rather than navigating to it.
export function portalDocumentPath(
	projectId: number | string,
	quoteId: number | string,
	mode: PortalDocumentMode = 'view',
	target: PortalDocumentTarget = 'proposal'
): string {
	return `/project/${encodeURIComponent(projectId)}/document/${encodeURIComponent(quoteId)}`
		+ `?target=${encodeURIComponent(target)}&mode=${encodeURIComponent(mode)}`;
}

// Opens documents in new tabs and, once the customer comes back to this tab, runs
// `reload` — an accept or a comment made over there changes the list, and nothing
// else would tell us.
//
// Owned by the page component: construct it in ngOnInit, destroy it in ngOnDestroy.
export class PortalDocumentOpener {

	private pending = false;
	private readonly onVisible = () => {
		if (this.pending && !document.hidden) {
			this.pending = false;
			this.reload();
		}
	};

	// projectId is public and reassignable: the page components keep one opener for
	// their lifetime and repoint it when the route's :id changes.
	//
	// `resolveUrl`, when given, replaces the portal's document page with the document's own URL.
	// The staff preview passes it, because the portal page cannot work in that new tab: a preview's
	// token lives in the memory of the frame WeQuote opened, so an unframed tab has no session and
	// would land on the login page. The document URL has its own token and stands alone — and in a
	// preview the backend returns the read-only /preview variant of it.
	constructor(
		public projectId: string,
		private reload: () => void,
		private resolveUrl: PortalDocumentUrlResolver | null = null
	) {
		document.addEventListener('visibilitychange', this.onVisible);
	}

	open(quoteId: number | string, mode: PortalDocumentMode = 'view', target: PortalDocumentTarget = 'proposal'): void {
		if (!quoteId) return;

		if (this.resolveUrl) {
			// Opened empty first, while still inside the click: the URL takes an API round trip to
			// arrive, and by then a popup blocker would have refused the window.
			const tab = window.open('', '_blank');

			this.resolveUrl(quoteId, mode, target, (url) => {
				if (!tab) return;
				if (!url) { tab.close(); return; }
				tab.opener = null;
				tab.location.replace(url);
			});
			return;
		}

		this.pending = true;
		window.open(portalDocumentPath(this.projectId, quoteId, mode, target), '_blank', 'noopener');
	}

	destroy(): void {
		document.removeEventListener('visibilitychange', this.onVisible);
	}
}
